import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { Loader2, CheckCircle2, XCircle, Ticket, Download, Calendar, MapPin, HelpCircle, Ban } from "lucide-react";import { endpoints, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { waSupportLink } from "@/lib/support";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "");

const StatusBadge = ({ status }) => {
  const map = {
    used: { c: "bg-slate-700 text-slate-300", t: "Used / Scanned", I: XCircle },
    issued: { c: "bg-emerald-500/15 text-emerald-400", t: "Valid — not used", I: CheckCircle2 },
    cancelled: { c: "bg-red-500/15 text-red-400", t: "Cancelled", I: Ban },
  };
  const s = map[status] || map.issued;
  return <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold ${s.c}`} data-testid="ticket-status-badge"><s.I className="w-4 h-4" /> {s.t}</span>;
};

export function TicketView() {
  const { code } = useParams();
  const [t, setT] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { endpoints.ticketByCode(code).then((r) => setT(r.data)).catch(() => {}).finally(() => setLoading(false)); }, [code]);

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-[#E11D2A]" /></div>;
  if (!t) return <div className="text-center py-32 text-slate-500">Ticket not found.</div>;
  const cancelled = t.status === "cancelled";
  const used = t.status === "used";

  return (
    <div className="px-4 max-w-md mx-auto py-10">
      <div className="rounded-2xl overflow-hidden border border-[#273042] bg-[#121620]" data-testid="ticket-view">
        <div className="bg-[#E11D2A] p-4 text-white flex items-center justify-between">
          <span className="font-extrabold">B&B KIGALI</span>
          <span className="text-xs font-bold">{t.isInvitation ? "INVITATION" : "TICKET"}</span>
        </div>
        <div className="p-6 text-center">
          <h1 className="text-xl font-extrabold text-white">{t.eventTitle}</h1>
          <div className="text-slate-400 text-sm mt-1">{t.categoryName} · {t.holderName}</div>
          {t.seat && <div className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#93B4FF]/15 text-[#93B4FF] text-sm font-bold" data-testid="ticket-seat"><MapPin className="w-3.5 h-3.5" /> Seat {t.seat}</div>}
          <div className="mt-3 flex flex-col items-center gap-1 text-xs text-slate-400">
            {t.startAt && <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {fmt(t.startAt)}</span>}
            {t.venue && <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {t.venue}{t.location ? `, ${t.location}` : ""}</span>}
          </div>
          <div className={`my-6 flex justify-center ${cancelled ? "opacity-30 grayscale" : ""}`}><div className="bg-white p-4 rounded-2xl"><QRCodeCanvas value={t.code} size={200} data-testid="ticket-qr" /></div></div>
          <div className="text-[#F5B301] font-extrabold text-lg tracking-wide" data-testid="ticket-number">{t.ticketNumber}</div>
          <div className="text-[11px] text-slate-500 mt-1">Order / ticket no: {t.orderNumber || t.ticketNumber} · Payment: <span className="uppercase font-semibold">{t.paymentStatus}</span></div>
          <div className="mt-4"><StatusBadge status={t.status} /></div>
          {used && t.checkedInAt && <p className="text-[11px] text-slate-500 mt-2" data-testid="ticket-checkin-time">Scanned on {fmt(t.checkedInAt)}</p>}
          {cancelled && <p className="text-[11px] text-red-400 mt-2">Cancelled{t.cancelReason ? ` — ${t.cancelReason}` : ""}. This ticket is no longer valid.</p>}
          {!cancelled && <p className="text-[11px] text-slate-500 mt-4">Present this QR code at the entrance. Valid for one entry only.</p>}
          <div className="mt-6 flex flex-col gap-2">
            {!cancelled && (
              <a href={`${API_BASE}/api/tickets/${t.code}.pdf`} target="_blank" rel="noopener noreferrer" data-testid="ticket-download-pdf"
                className="h-11 rounded-full bg-white/5 border border-[#273042] text-white font-semibold flex items-center justify-center gap-2 hover:bg-white/10 transition-colors"><Download className="w-4 h-4" /> Download PDF</a>
            )}
            <a href={waSupportLink(`Hello B&B KIGALI support, I need help with my ticket ${t.ticketNumber} (${t.eventTitle}).`)}
              target="_blank" rel="noopener noreferrer" data-testid="ticket-support-link"
              className="h-11 rounded-full text-[#25D366] font-semibold flex items-center justify-center gap-2 hover:underline"><HelpCircle className="w-4 h-4" /> Need help with this ticket?</a>
          </div>
        </div>
      </div>
    </div>
  );
}

export function MyTickets() {
  const { isAuthed } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { if (!isAuthed) { setLoading(false); return; } endpoints.myTickets().then((r) => setTickets(r.data.tickets || [])).catch(() => {}).finally(() => setLoading(false)); }, [isAuthed]);

  if (!isAuthed) return <div className="text-center py-32 text-slate-400">Please <button onClick={() => window.dispatchEvent(new Event("open-auth"))} className="text-[#E11D2A] font-semibold">sign in</button> to see your tickets.</div>;
  if (loading) return <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-[#E11D2A]" /></div>;

  return (
    <div className="px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto py-10">
      <div className="flex items-center gap-2 mb-2"><Ticket className="w-7 h-7 text-[#E11D2A]" /><h1 className="text-3xl font-extrabold text-white">My Tickets</h1></div>
      <p className="text-slate-400 text-sm mb-6">Your ticket statement — reopen, download or show the QR at the entrance.</p>
      {tickets.length === 0 ? <div className="text-center py-16 text-slate-500" data-testid="my-tickets-empty">No tickets yet. <Link to="/events" className="text-[#E11D2A]">Browse events</Link></div> : (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="my-tickets">
          {tickets.map((t) => {
            const cancelled = t.status === "cancelled";
            return (
              <Link key={t.id} to={`/ticket/${t.code}`} data-testid={`my-ticket-${t.id}`} className="flex items-start gap-4 rounded-xl border border-[#273042] bg-[#121620] p-4 card-hover">
                <div className={`bg-white p-2 rounded-lg shrink-0 ${cancelled ? "opacity-30 grayscale" : ""}`}><QRCodeCanvas value={t.code} size={64} /></div>
                <div className="min-w-0 flex-1">
                  <div className="text-white font-bold truncate">{t.eventTitle}</div>
                  <div className="text-xs text-slate-400">{t.categoryName} · {t.holderName}</div>
                  {t.seat && <div className="text-[11px] text-[#93B4FF] font-bold mt-0.5" data-testid={`my-ticket-seat-${t.id}`}>Seat {t.seat}</div>}
                  <div className="text-[#F5B301] text-sm font-bold mt-1">{t.ticketNumber}</div>
                  <div className="text-[10px] text-slate-500">Payment: <span className="uppercase font-semibold">{t.paymentStatus}</span></div>
                  <div className="mt-1.5 flex items-center gap-2">
                    <span className={`text-[10px] font-bold uppercase ${cancelled ? "text-red-400" : t.status === "used" ? "text-slate-500" : "text-emerald-400"}`} data-testid={`my-ticket-status-${t.id}`}>
                      {cancelled ? "Cancelled" : t.status === "used" ? "Used" : "Valid"}
                    </span>
                    {t.status === "used" && t.checkedInAt && <span className="text-[10px] text-slate-500">· {fmt(t.checkedInAt)}</span>}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
